import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, MapPin, Users, User, ChevronDown, X, UserPlus, ArrowRight, LogOut, Edit3, FileText, Database, Phone, Briefcase, GraduationCap, Calendar, Heart, Lock } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import DISTRICTS_BY_STATE from '../../data/indiaDistricts';
import PaginationBar from '../../components/PaginationBar';
import { MemberSections, FamilyTopView } from '../../components/FamilySections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GENDER_OPTIONS = ['Male / पुरुष', 'Female / महिला'];
const AGE_RANGES = [
  { label: 'All Ages', min: '', max: '' },
  { label: '0-18', min: 0, max: 18 },
  { label: '18-25', min: 18, max: 25 },
  { label: '25-35', min: 25, max: 35 },
  { label: '35-50', min: 35, max: 50 },
  { label: '50-65', min: 50, max: 65 },
  { label: '65+', min: 65, max: 100 },
];

const FamiliesRegisterPage = () => {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ states: [], nukhs: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    district: '',
    nukh: '',
    city: '',
    gender: '',
    ageRange: '',
    education: '',
    occupation: '',
    maritalStatus: '',
  });
  
  // Pagination state
  const [totalFamilies, setTotalFamilies] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  
  // Modal state for family details
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const navigate = useNavigate();

  // Function to get current token from any login type
  const getActiveToken = () => {
    return localStorage.getItem('familyToken') || 
           localStorage.getItem('professionalToken') || 
           localStorage.getItem('institutionalToken') || 
           localStorage.getItem('token'); // Admin uses generic 'token' key
  };

  // Function to get display name
  const getDisplayName = () => {
    return localStorage.getItem('familyHeadName') || 
           localStorage.getItem('professionalName') || 
           localStorage.getItem('institutionalName') || 
           'User';
  };

  // Check login status dynamically
  const [isLoggedIn, setIsLoggedIn] = useState(!!getActiveToken());
  const [displayName, setDisplayName] = useState(getDisplayName());

  // Re-check login status when component mounts or when returning from login
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = getActiveToken();
      setIsLoggedIn(!!token);
      setDisplayName(getDisplayName());
    };
    
    // Check on mount
    checkLoginStatus();
    
    // Also check when window gains focus (user might have logged in in another tab)
    window.addEventListener('focus', checkLoginStatus);
    
    // Check periodically to catch login state changes
    const interval = setInterval(checkLoginStatus, 1000);
    
    return () => {
      window.removeEventListener('focus', checkLoginStatus);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    // Clear all possible tokens
    localStorage.removeItem('familyToken');
    localStorage.removeItem('familyId');
    localStorage.removeItem('familyHeadName');
    localStorage.removeItem('familyEmail');
    localStorage.removeItem('professionalToken');
    localStorage.removeItem('professionalId');
    localStorage.removeItem('professionalName');
    localStorage.removeItem('institutionalToken');
    localStorage.removeItem('institutionalId');
    localStorage.removeItem('institutionalName');
    localStorage.removeItem('token'); // Admin token
    setIsLoggedIn(false);
    setDisplayName('');
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Fetch family details for modal
  const fetchFamilyDetails = async (familyId) => {
    setModalLoading(true);
    const currentToken = getActiveToken();
    
    if (!currentToken) {
      setShowLoginPrompt(true);
      setModalLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/families/directory/${familyId}`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedFamily(data);
      } else if (response.status === 401) {
        toast.error('Please login to view family details');
        setShowLoginPrompt(true);
      } else {
        toast.error('Failed to load family details');
      }
    } catch (error) {
      console.error('Error fetching family details:', error);
      toast.error('Failed to load family details');
    } finally {
      setModalLoading(false);
    }
  };

  const openFamilyModal = (family) => {
    const currentToken = getActiveToken();
    if (!currentToken) {
      setShowLoginPrompt(true);
      return;
    }
    fetchFamilyDetails(family.familyId || family.id);
  };

  const closeFamilyModal = () => {
    setSelectedFamily(null);
  };

  useEffect(() => {
    fetchOptions();
    fetchFamilies();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/family-registration/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFamilies = async (page = 1) => {
    setLoading(true);
    
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.state) params.append('state', filters.state);
      if (filters.district) params.append('district', filters.district);
      if (filters.nukh) params.append('nukh', filters.nukh);
      if (filters.city) params.append('city', filters.city);
      if (filters.gender) params.append('gender', filters.gender);
      if (filters.education) params.append('education', filters.education);
      if (filters.occupation) params.append('occupation', filters.occupation);
      if (filters.maritalStatus) params.append('marital_status', filters.maritalStatus);
      if (filters.ageRange) {
        const range = AGE_RANGES.find(r => r.label === filters.ageRange);
        if (range && range.min !== '') {
          params.append('min_age', range.min);
          params.append('max_age', range.max);
        }
      }
      params.append('limit', PAGE_SIZE.toString());
      params.append('skip', skip.toString());

      const response = await fetch(`${BACKEND_URL}/api/families/directory?${params}`);
      if (response.ok) {
        const data = await response.json();
        setFamilies(data.families);
        setTotalFamilies(data.total);
        setCurrentPage(page);
        // Scroll to top of results
        window.scrollTo({ top: 300, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalFamilies / PAGE_SIZE);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchFamilies(page);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchFamilies(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', state: '', district: '', nukh: '', city: '', gender: '', ageRange: '', education: '', occupation: '', maritalStatus: '' });
    setCurrentPage(1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchFamilies(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.state, filters.nukh, filters.district, filters.city, filters.gender, filters.ageRange, filters.education, filters.occupation, filters.maritalStatus]);

  // Get family head name
  const getFamilyHead = (family) => {
    const head = family.familyMembers?.find(m => m.relationWithHead === 'Head - Self');
    return head?.name || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="gradient-orange py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <a href="/" className="hover:text-white">Home</a>
              <span>›</span>
              <span>Families Directory</span>
            </div>
            
            {/* User Info & Logout for logged-in family users */}
            {isLoggedIn && (
              <div className="flex items-center gap-3">
                <span className="text-white/90 text-sm hidden sm:inline">
                  Welcome, <strong>{displayName}</strong>
                </span>
                <Link to="/family/edit">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit My Family
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="family-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            परिवार डेटा / Families Data
          </h1>
          <p className="text-white/90">
            Digital family register and directory
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-transparent p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">परिवारों का डिजीटल डेटा</h2>
            </div>
            <p className="text-gray-600 mt-3">
              परिवारों व परिवार के सदस्यों का विवरण फीड करने हेतु यहाँ फॉर्म दिया जा रहा है। इसे भरकर परिवारों का डिजीटल डेटा तैयार करने में योगदान दीजिये।
            </p>
          </div>

          <div className="p-6">
            <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-xl p-6 text-center">
              <Link to="/family-registration" className="inline-block w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white px-6 sm:px-8 py-3 text-base sm:text-lg whitespace-normal h-auto">
                  <FileText className="w-5 h-5 mr-2 flex-shrink-0" />
                  परिवार फॉर्म भरें / Fill Family Form
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Family Directory Section */}
      <section className="bg-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">परिवार निर्देशिका / Family Directory</h2>
            <p className="text-gray-600">Search and connect with registered families in our community</p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mx-auto mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by family name, location, Nukh..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 h-12 bg-white"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-secondary hover:bg-secondary/90">
              Search
            </Button>
          </form>

        {/* Filter Toggle & Active Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={16} />
            Filters
            <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>

          {(filters.state || filters.nukh || filters.district || filters.gender || filters.ageRange || filters.education || filters.occupation || filters.city || filters.maritalStatus) && (
            <>
              {filters.state && (
                <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  State: {filters.state}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('state', '')} />
                </span>
              )}
              {filters.district && (
                <span className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  District: {filters.district}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('district', '')} />
                </span>
              )}
              {filters.city && (
                <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  City: {filters.city}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('city', '')} />
                </span>
              )}
              {filters.nukh && (
                <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Nukh: {filters.nukh}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('nukh', '')} />
                </span>
              )}
              {filters.gender && (
                <span className="bg-pink-50 text-pink-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Gender: {filters.gender}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('gender', '')} />
                </span>
              )}
              {filters.ageRange && (
                <span className="bg-amber-50 text-amber-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Age: {filters.ageRange}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('ageRange', '')} />
                </span>
              )}
              {filters.education && (
                <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Education: {filters.education}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('education', '')} />
                </span>
              )}
              {filters.occupation && (
                <span className="bg-orange-50 text-orange-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Occupation: {filters.occupation}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('occupation', '')} />
                </span>
              )}
              {filters.maritalStatus && (
                <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  Marital Status: {filters.maritalStatus}
                  <X size={14} className="cursor-pointer" onClick={() => handleFilterChange('maritalStatus', '')} />
                </span>
              )}
              <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-primary">
                Clear all
              </button>
            </>
          )}

          <span className="ml-auto text-sm text-gray-500">
            Page {currentPage} of {totalPages} ({totalFamilies} families)
          </span>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100"
          >
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-4">Location Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-state"
                >
                  <option value="">All States</option>
                  {options.states?.map((state, idx) => (
                    <option key={idx} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <select
                  value={filters.district}
                  onChange={(e) => handleFilterChange('district', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  disabled={!filters.state}
                  data-testid="filter-district"
                >
                  <option value="">All Districts</option>
                  {(DISTRICTS_BY_STATE[filters.state] || []).map((d, idx) => (
                    <option key={idx} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City / Village</label>
                <Input
                  type="text"
                  placeholder="Enter city or village"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  data-testid="filter-city"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nukh</label>
                <select
                  value={filters.nukh}
                  onChange={(e) => handleFilterChange('nukh', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-nukh"
                >
                  <option value="">All Nukhs</option>
                  {options.nukhs?.map((nukh, idx) => (
                    <option key={idx} value={nukh}>{nukh}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-4 mt-2">Member Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-gender"
                >
                  <option value="">All</option>
                  {GENDER_OPTIONS.map((g, idx) => (
                    <option key={idx} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
                <select
                  value={filters.ageRange}
                  onChange={(e) => handleFilterChange('ageRange', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-age"
                >
                  {AGE_RANGES.map((r, idx) => (
                    <option key={idx} value={r.label === 'All Ages' ? '' : r.label}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <select
                  value={filters.education}
                  onChange={(e) => handleFilterChange('education', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-education"
                >
                  <option value="">All</option>
                  {options.education?.map((edu, idx) => (
                    <option key={idx} value={edu}>{edu}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                <select
                  value={filters.occupation}
                  onChange={(e) => handleFilterChange('occupation', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-occupation"
                >
                  <option value="">All</option>
                  {options.occupation?.map((occ, idx) => (
                    <option key={idx} value={occ}>{occ}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                <select
                  value={filters.maritalStatus}
                  onChange={(e) => handleFilterChange('maritalStatus', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm"
                  data-testid="filter-marital-status"
                >
                  <option value="">All</option>
                  {options.maritalStatus?.map((ms, idx) => (
                    <option key={idx} value={ms}>{ms}</option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}

        {/* Results Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : families.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Families Found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters, or register your family</p>
            <Link to="/family-registration">
              <Button className="bg-primary hover:bg-primary/90">
                <UserPlus className="w-4 h-4 mr-2" />
                Register Your Family
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {families.map((family, index) => (
              <motion.div
                key={family.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                data-testid={`family-card-${family.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{getFamilyHead(family)}</h3>
                    {family.familyId && (
                      <p className="text-primary font-medium text-sm">ID: {family.familyId}</p>
                    )}
                    <p className="text-gray-500 text-sm">{family.familyNukh}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} className="flex-shrink-0" />
                    <span>{family.district || family.currentPlace}, {family.state}</span>
                  </div>
                  {family.originPlace && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <span className="text-xs">Origin: {family.originPlace}</span>
                    </div>
                  )}
                </div>

                {/* Family Members Summary */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      <User size={14} className="inline mr-1" />
                      {family.totalMembers || family.familyMembers?.length || 0} members
                    </span>
                    <span className="text-xs text-gray-500">
                      {family.totalMale || 0}M / {family.totalFemale || 0}F
                    </span>
                  </div>
                  
                  {/* Show first few member names */}
                  {family.familyMembers && family.familyMembers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {family.familyMembers.slice(0, 3).map((member, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {member.name}
                        </span>
                      ))}
                      {family.familyMembers.length > 3 && (
                        <span className="text-xs text-gray-400">
                          +{family.familyMembers.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* View Details Link */}
                <div className="mt-4 pt-3 border-t border-dashed">
                  <button
                    onClick={() => openFamilyModal(family)}
                    className="w-full text-center text-primary hover:text-primary-600 text-sm font-medium flex items-center justify-center gap-1 py-2.5 min-h-[40px] hover:bg-primary/5 rounded transition-colors"
                    data-testid={`view-details-${family.id}`}
                  >
                    Click here to view details
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            totalItems={totalFamilies}
            pageSize={PAGE_SIZE}
            itemLabel="families"
            testIdPrefix="families"
          />
        )}

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-primary to-primary-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Register Your Family</h3>
          <p className="mb-6 text-white/90">Join our community directory and connect with other Khatri families</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/family-registration">
              <Button className="bg-white text-primary hover:bg-gray-100">
                <UserPlus className="w-4 h-4 mr-2" />
                Register Family
              </Button>
            </Link>
            <Link to="/auth?tab=family">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Already Registered? Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      </section>

      {/* Family Details Modal */}
      <AnimatePresence>
        {selectedFamily && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeFamilyModal}
            data-testid="family-details-modal-backdrop"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="family-details-modal"
            >
              {modalLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                          <Users className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">
                            {selectedFamily.familyMembers?.find(m => m.relationWithHead === 'Head - Self')?.name || 'Family Details'}
                          </h2>
                          {selectedFamily.familyId && (
                            <p className="text-white/90 text-sm mt-1">Family ID: {selectedFamily.familyId}</p>
                          )}
                          <p className="text-white/80 text-sm">{selectedFamily.familyNukh}</p>
                        </div>
                      </div>
                      <button
                        onClick={closeFamilyModal}
                        className="text-white/80 hover:text-white p-1"
                        data-testid="close-modal-btn"
                      >
                        <X size={24} />
                      </button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {/* Location & Family Info */}
                    <div className="mb-6">
                      <FamilyTopView family={selectedFamily} />
                    </div>

                    {/* Family Summary */}
                    <div className="mb-6">
                      <div className="flex items-center gap-4 bg-primary/5 rounded-xl p-4">
                        <div className="text-center flex-1">
                          <p className="text-2xl font-bold text-primary">{selectedFamily.totalMembers || selectedFamily.familyMembers?.length || 0}</p>
                          <p className="text-xs text-gray-600">Total Members</p>
                        </div>
                        <div className="text-center flex-1 border-l border-primary/20">
                          <p className="text-2xl font-bold text-blue-600">{selectedFamily.totalMale || 0}</p>
                          <p className="text-xs text-gray-600">Male</p>
                        </div>
                        <div className="text-center flex-1 border-l border-primary/20">
                          <p className="text-2xl font-bold text-pink-600">{selectedFamily.totalFemale || 0}</p>
                          <p className="text-xs text-gray-600">Female</p>
                        </div>
                      </div>
                    </div>

                    {/* Family Members */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <User className="w-5 h-5 text-primary" />
                        Family Members
                      </h3>
                      <div className="space-y-4">
                        {selectedFamily.familyMembers?.map((member, idx) => {
                          const isHead = member.relationWithHead === 'Head - Self';
                          return (
                            <div
                              key={idx}
                              className={`rounded-xl p-4 border ${isHead ? 'bg-primary/5 border-primary/20' : 'bg-gray-50 border-gray-100'}`}
                              data-testid={`member-card-${idx}`}
                            >
                              {/* Member Header */}
                              <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-bold text-gray-900 text-lg">{member.name}</h4>
                                    {isHead && (
                                      <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">Head</span>
                                    )}
                                    {member.memberId && (
                                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">ID: {member.memberId}</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{member.relationWithHead}</p>
                                </div>
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${member.gender?.toLowerCase() === 'male' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>
                                  <User size={20} />
                                  <span className="sr-only">{member.gender}</span>
                                </div>
                              </div>

                              {/* Sectioned member details */}
                              <MemberSections member={member} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="border-t p-4 bg-gray-50">
                    <Button
                      onClick={closeFamilyModal}
                      className="w-full bg-primary hover:bg-primary/90"
                      data-testid="close-modal-footer-btn"
                    >
                      Close
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Prompt Modal */}
      <AnimatePresence>
        {showLoginPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLoginPrompt(false)}
            data-testid="login-prompt-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-6 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold">Login Required</h2>
                <p className="text-white/80 mt-2">लॉगिन आवश्यक है</p>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 text-center mb-6">
                  परिवार का पूरा विवरण देखने के लिए कृपया पहले लॉगिन करें।
                  <br />
                  <span className="text-sm text-gray-500">
                    Please login to view complete family details.
                  </span>
                </p>
                
                <div className="space-y-3">
                  <Link to="/auth?tab=family" className="block">
                    <Button className="w-full bg-primary hover:bg-primary-600">
                      <Lock className="w-4 h-4 mr-2" />
                      Login Now
                    </Button>
                  </Link>
                  
                  <Link to="/family-registration" className="block">
                    <Button variant="outline" className="w-full">
                      <UserPlus className="w-4 h-4 mr-2" />
                      New Registration
                    </Button>
                  </Link>
                  
                  <Button 
                    variant="ghost" 
                    className="w-full text-gray-500"
                    onClick={() => setShowLoginPrompt(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FamiliesRegisterPage;
